<template lang="pug">
AuthorDialog(
  :open="toValue(open)"
  @close="() => emit('close')"
  :class="[submitStatus === 'success' && translationStatus === 'success' ? 'primary-green' : dest === 'production' ? 'primary-amber' : 'primary-sky']"
)
  form.prose.prose-sm.dark_prose-invert.w-fill.max-w-none.z-10
    .grid.w-fill.gap-8(
      :class="[courseType === 'VinciWorks' ? 'xl_grid-cols-3' : 'xl_grid-cols-2']"
    )
      .p-4
        h3 1. Select destination
      
        fieldset.my-4.inline-flex.flex-col.gap-2(
          :disabled="submitStatus === 'pending' || submitStatus === 'success'"
        )
          label.inline-flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(
              name="dest"
              type="radio"
              v-model="dest"
              value="test"
            )
            span Internal review

          label.inline-flex.gap-1.items-center
            input.focus-visible_ring-4.transition.rounded-full(
              name="dest"
              type="radio"
              v-model="dest"
              value="production"
            )
            span Live for clients

        .flex.p-4.gap-4.bg-primary-500.bg-opacity-20.text-primary-800.dark_text-primary-300.not-prose.border-2.border-primary-500.border-opacity-50.rounded(
          :class="[submitStatus === 'success' ? 'primary-green' : dest === 'production' ? 'primary-amber' : 'primary-sky']"
        )
          .flex-0.flex.items-center.justify-center.text-4xl.px-1.text-primary-500.dark_text-primary-400
            Icon.animate-spin(icon="ph:spinner" v-if="submitStatus === 'pending'")
            Icon(icon="ph:check-circle" v-else-if="submitStatus === 'success'")

            span.animate-rumble-fast.text-primary-700.dark_text-primary-400.origin-bottom-left(
              v-else-if="dest === 'production'"
            )
              Icon(:icon="'ph:truck-duotone'")

            span.animate-rumble.text-primary-700.dark_text-primary-400.origin-top-right(
              v-else-if="dest === 'test'"
            )
              Icon(:icon="'ph:test-tube-duotone'")
          .m-0.text-sm
            p.max-w-full(class="[text-wrap:balance]" v-if="submitStatus === 'success'") 
              | The latest commit is now published at #[a.text-primary-800(:href="url" rel="noopener" target="_blank") {{ shortUrl }}].
            p.max-w-full(
              class="[text-wrap:balance]"
              v-else-if="submitStatus === 'pending'"
            ) 
              | Publishing to #[a.text-primary-800(:href="url" rel="noopener" target="_blank") {{ shortUrl }}]...
            p.max-w-full(v-else="")
              | Press "Publish" to publish this {{ courseType }} course to #[a.text-primary-800.dark_text-primary-400(:href="url" rel="noopener" target="_blank") {{ shortUrl }}].

          //- copy URL to clipboard
          button.btn.bg-primary-300.dark_bg-primary-700.text-primary-900.dark_text-primary-100.aspect-square.flex.items-center.justify-center.font-bold.text-2xl.p-2(
            @click="copyToClipboard(url)"
            type="button"
            aria-label="Copy publish URL"
            title="Copy publish URL"
          )
            Icon(:icon="'ph:copy-duotone'")

      .p-4
        h3 2. Summarize changes 
        fieldset(:disabled="submitStatus === 'pending' || translationStatus === 'pending' || (submitStatus === 'success' && translationStatus === 'success')")
          label 
            p.font-bold a. Extent of content change

            //- radio for major/minor
            p.flex.grid.grid-cols-2.gap-3.p-4.bg-primary-800.bg-opacity-10.rounded-sm
              label.flex.items-center.gap-1
                input(type="radio" value="major" v-model="formState.level")
                div.px-1.leading-tight 
                  div Significant changes 
                  .text-xs.italic (shown to clients)

              label.flex.items-center.gap-1
                input(type="radio" value="minor" v-model="formState.level")
                div.px-1.leading-tight 
                  div Small changes 
                  .text-xs.italic (hidden from clients)

              label.flex.items-center.gap-1
                input(type="radio" value="review" v-model="formState.level")
                div.px-1.leading-tight 
                  div Reviewed content
                  .text-xs.italic (look fresh for clients)

              label.flex.items-center.gap-1(v-if="draft.firmCode")
                input(type="radio" value="custom" v-model="formState.level")
                div.px-1.leading-tight 
                  div Customisation work

          label  
            p.font-bold b. Describe the changes

            p.text-xs.m-0(:class="{ hidden: isReviewedContent }")
              span.my-2.italic Start with a preset message: &nbsp;
              select.m-2(value="" v-model="selectedPreset" ).dark_bg-gray-700.dark_text-gray-300.p-1
                option.p-3(disabled value="") Choose a preset...
                option.p-3(v-for="preset in presets" :value="preset.label") {{ preset.label }}...
                option.p-3.italics.text-gray-500(value="..." v-if="draft.firmCode") ✏️ Write from scratch...

              
            p.mt-0
              textarea.w-full.resize-y(v-model="formState.message" :disabled="!selectedPreset || isReviewedContent" :placeholder="selectedPreset === '' ? 'Choose a preset...' : 'Write a message for clients...'").dark_bg-gray-700.dark_text-gray-300.p-2.disabled_opacity-50

            
          
            div
              div(v-if="formState.level === 'minor'") 
                p.italic 🚩🚩🚩 This publish event will be hidden from clients.
              div(v-else)
                p.italic 🚩🚩🚩 What clients will see in the portal timeline:
                .text-xs.bg-primary-500.bg-opacity-20.p-2.rounded.m-2
                  .font-semibold {{ portalMessages[formState.level] }}
                  .font-light {{ formState.message }}

          label
            p.font-bold c. Internal notes 
            
            p.italic  These notes are for internal use only and will not be shown to clients.

            textarea.w-full.resize-y(v-model="formState.internal").dark_bg-gray-700.dark_text-gray-300.p-2


      .p-4(v-if="courseType === 'VinciWorks'")
        h3 3. Update customisations

        .prose.dark_prose-invert.max-w-full
          p.italic.opacity-80.text-sm
            | Please check the following customisations for compatibility with any changes made to this base course and update them if necessary.

        .p-4.bg-black.bg-opacity-20.resize-y.overflow-y-auto(v-if="courseType === 'VinciWorks'")
          CustomReport.text-center(:report="customReport")

      .p-4(v-if="courseType === 'VinciWorks'")
        h3 4. Update and publish AI translations

        div(v-if="allTranslations.length > 0")
          fieldset(
            :disabled="submitStatus === 'pending' || translationStatus === 'pending' || (submitStatus === 'success' && translationStatus === 'success')"
          )
            .prose.dark_prose-invert.max-w-full
              p.italic.opacity-80.text-sm
                | Select these to rerun and publish AI translations for this course now. This may take several minutes. Force AI translations is disabled.
              p.italic.opacity-80.text-sm
                | Translations will be processed in the background — you will be notified of any errors by email. Existing translations will not be refreshed.

              //- the translation provider choice
              p.mt-2.max-w-lg
                label
                  span Translation Provider: 
                  select.rounded.px-2.py-1.ms-2(v-model="translationProvider")
                    option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="azure") Azure
                    option.text-gray-900.dark_text-gray-100.bg-white.dark_bg-gray-900(value="openai") OpenAI

              p.mt-2.max-w-lg
                label
                  input(type="checkbox" v-model="translateBaseCourse")
                  span &nbsp; Translate base course

              p.mt-2.max-w-lg
                label
                  input(type="checkbox" :disabled="!translateBaseCourse" v-bind="selectAllTranslations()")
                  span &nbsp; Select {{ availableTranslations.length }} of {{ allTranslations.length }} translations:
              //- p {{ translationStatus }} ({{ availableTranslations.length }} remaining)

              .h-50.overflow-y-auto.resize-y.mt-2
                table.m-0 
                  thead
                    tr
                      th(class="!p-1") Select
                      th(class="!p-1") Course
                      th(class="!p-1") Lang
                  tbody
                    tr(v-for="translation in allTranslations")
                      td(class="!p-1")
                        input(type="checkbox" :disabled="!translateBaseCourse" :value="`${translation.firmCode}_${translation.courseId}_${translation.to}`" v-model="selectedTranslations")
                      td(class="!p-1") {{ translation.firmCode }}_{{ translation.courseId }}
                      td(class="!p-1") {{ translation.to }}
            
          //- button.btn.btn-primary.mt-2(@click="() => emit('publishTranslations')") Publish translations

    fieldset.flex.justify-center.gap-2(
      :disabled="submitStatus === 'pending' || translationStatus === 'pending'"
    )
      button.btn.btn-primary(
        @click="submit"
        v-if="!(submitStatus === 'success' && translationStatus === 'success')"
      ) Publish
      button.btn(@click="() => emit('close')") Close
</template>

<script setup lang="ts">
import { computed, ref, watch, toValue, reactive, nextTick } from 'vue';
import {
  publishCourse,
  submitBulkTranslationJob
} from '../lib/callModContentAuthors';
import type { Ref } from 'vue';
import CustomReport from './CustomReport.vue';
import { Icon } from '@iconify/vue';
import AuthorDialog from './AuthorDialog.vue';
import getJWT from '../../lib/getJWT';

const emit = defineEmits(['close', 'success']);
const submitStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');
const translationStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');
const dest = ref<'production' | 'test'>('test');
const error = ref('');
const translationProvider = ref('openai');
// Always translate the base course before customisations: on by default, and a
// prerequisite for selecting any customisation translations (see the watch below).
const translateBaseCourse = ref(true);

const props = defineProps<{
  open: boolean | Ref<boolean>;
  draft: CourseDraft | null;
  customReport: CustomReport | null;
}>();

const portalMessages = {
  major: 'VinciWorks edited this course',
  review: 'VinciWorks reviewed this course',
  custom: 'The customisation team edited this course'
};

const selectedPreset = ref('');

const presets = [
  {
    label: 'Minor content update',
    text: 'We’ve made minor changes to improve the quality of the content.'
  },
  {
    label: 'Regulatory update',
    text: 'This course has been updated to reflect recent regulatory changes.'
  },
  {
    label: 'New module added',
    text: 'A new module has been added to expand the course.'
  },
  {
    label: 'Improved aesthetics',
    text: 'We’ve improved the visuals and aesthetics for a better learning experience.'
  },
  // See isReviewedContent below
  // {
  //   label: 'Content review',
  //   text: 'We’ve reviewed the course to ensure the content remains relevant and accurate.'
  // },
  {
    label: 'Content improvement',
    text: 'We’ve made improvements to the course content based on user feedback.'
  },
  {
    label: 'New assessment questions',
    text: 'The assessment section has been updated with new questions.'
  },
  {
    label: 'Major content overhaul',
    text: 'The course has undergone a major overhaul with new and improved content.'
  },
  {
    label: 'Customisation work',
    text: 'We’ve made customisations to this course based on client requirements.'
  },
  {
    label: 'New videos',
    text: 'We updated the course’s videos to improve the quality of the video experience; the learning outcomes have not changed.'
  }
];

const formState = reactive<Partial<PublishBody>>({
  level: 'minor',
  // messageDropdown: '',
  // messageTextarea: '',
  message: '',
  internal: ''
});

const isReviewedContent = computed(() => formState.level === 'review');

watch(isReviewedContent, val => {
  if (val)
    formState.message =
      'We’ve reviewed this course to ensure it’s all up to date - no changes were required.';
});

watch(selectedPreset, val => {
  const preset = presets.find(p => p.label === val);
  if (preset) formState.message = preset.text;
});

// const message = computed(() =>
//   formState.messageDropdown === '...'
//     ? formState.messageTextarea
//     : formState.messageDropdown
// );

const getPublishData = () => ({ ...formState });

const selectAllTranslations = () => ({
  checked:
    selectedTranslations.value.length === availableTranslations.value.length,
  onChange: (e: Event) => {
    const checked = (e.target as HTMLInputElement).checked;
    selectedTranslations.value = checked
      ? availableTranslations.value.map(
          t => `${t.firmCode}_${t.courseId}_${t.to}`
        )
      : [];
  }
});

const selectedTranslations = ref<string[]>([]);

const availableTranslations = computed(() => allTranslations.value);

const allTranslations = computed(() => {
  let translations: TranslationParams[] = [];

  // loop through custom report list
  if (props.customReport) {
    for (const item of props.customReport?.list || [])
      for (const language of item?.languages || []) {
        translations.push({
          firmCode: item.firmCode,
          courseId: item.courseId,
          to: language
        });
      }
  }

  return translations;
});

// The languages the base course is translated into when "Translate base course"
// is on: every distinct language across all customisations.
const baseLanguages = computed<string[]>(() => [
  ...new Set(allTranslations.value.map(t => t.to))
]);

// Customisations can only be translated when the base course is also being
// translated, so unchecking the base clears any selected customisations.
watch(translateBaseCourse, on => {
  if (!on) selectedTranslations.value = [];
});

// Build the bulk translation jobs to submit. A single bulk job is a cross-product
// (courses × languages), so we submit one base job (all distinct languages) first,
// then one job per firm with only that firm's languages and skipBaseTranslation set
// (the base is already handled by the base job; FIFO ordering guarantees it runs first).
const buildBulkJobs = () => {
  if (!props.draft) return [];

  const selected = availableTranslations.value.filter(t =>
    selectedTranslations.value.includes(`${t.firmCode}_${t.courseId}_${t.to}`)
  );

  const common = {
    provider: translationProvider.value,
    refreshAi: false,
    publishToProduction: dest.value === 'production',
    notifyEmail: getJWT().userProfile?.email || 'customisations@vinciworks.com'
  };

  const jobs: any[] = [];

  // Base course first (it's a prerequisite for the customisations). Translated
  // into every customisation language whenever the checkbox is on.
  if (translateBaseCourse.value && baseLanguages.value.length > 0) {
    jobs.push({
      courses: [{ courseId: props.draft.courseId, firmCode: '' }],
      languages: baseLanguages.value,
      ...common
    });
  }

  if (selected.length === 0) return jobs;

  const byFirm = new Map<
    string,
    { courseId: number; firmCode: string; langs: Set<string> }
  >();
  for (const t of selected) {
    const key = `${t.firmCode}_${t.courseId}`;
    if (!byFirm.has(key))
      byFirm.set(key, {
        courseId: t.courseId,
        firmCode: t.firmCode ?? '',
        langs: new Set()
      });
    byFirm.get(key)!.langs.add(t.to);
  }
  const firmJobs = [...byFirm.values()].map(c => ({
    courses: [{ courseId: c.courseId, firmCode: c.firmCode }],
    languages: [...c.langs],
    skipBaseTranslation: true,
    ...common
  }));

  return [...jobs, ...firmJobs];
};

const url = computed(() => {
  if (!props.draft) return '';

  // Check if this is a conversational learning course
  const isConversational =
    props.draft.activityName === 'Conversational Learning';
  const { courseId, firmCode } = props.draft;

  if (isConversational) {
    const internalQ = dest.value === 'test' ? '?internal=true' : '';
    const firm = firmCode || 'demo';
    return `https://chat.vinciworks.com/course/${firm}/${courseId}/intro/welcome${internalQ}`;
  }

  // Original UberCourse logic
  const { ubercourseVersion } = props.draft;
  const internalQ = dest.value === 'test' ? '&internal=true' : '';
  const origin = window.location.origin
    .replace('authors.', 'portal.')
    .replace('localdev.', 'portal.');
  const firmQ = firmCode ? `&firm=${firmCode}` : '';

  return `${origin}/awscontent/ubercourse/${ubercourseVersion}/app.html?courseid=${courseId}${firmQ}${internalQ}`;
});

const shortUrl = computed(() => url.value.split('.com/')[1]);

const courseType = computed(() => {
  if (!props.draft) return '';

  // Check product type first
  const isConversational =
    props.draft.activityName === 'Conversational Learning';
  if (isConversational)
    return props.draft.type === 'course'
      ? 'Conversational Learning'
      : `${props.draft.type} Conversational Learning`;

  // Original type-based logic for UberCourse
  return props.draft.type === 'course' ? 'VinciWorks' : props.draft.type;
});

const yamlName = computed(() =>
  props.draft
    ? props.draft?.firmCode
      ? `${props.draft?.firmCode}_${props.draft?.courseId}.yml`
      : `${props.draft?.courseId}.yml`
    : ''
);

watch(
  () => toValue(props.open),
  o => {
    if (o) {
      submitStatus.value = 'idle';
      translationStatus.value = 'idle';
      dest.value = 'test';
      translateBaseCourse.value = true;
      selectedTranslations.value = availableTranslations.value.map(
        t => `${t.firmCode}_${t.courseId}_${t.to}`
      );
    }
  }
);

const close = () => {
  emit('close');
};

const submit = async () => {
  if (!props.draft) return;

  submitStatus.value = 'pending';
  await new Promise(r => setTimeout(r, 2e3));

  const publish = dest.value === 'production' ? publishLive : publishInternal;

  try {
    await publish(props.draft);
    submitStatus.value = 'success';

    const jobs = buildBulkJobs();
    if (jobs.length > 0) {
      translationStatus.value = 'pending';
      try {
        // Submit sequentially (not Promise.all): the FIFO queue dedupes on a
        // millisecond timestamp, so same-instant sends would be dropped. Sequential
        // awaits also preserve base-job-first ordering.
        for (const job of jobs) await submitBulkTranslationJob(job);
        translationStatus.value = 'success';
      } catch (e) {
        translationStatus.value = 'error';
      }
    } else {
      // no translations to publish or none selected
      translationStatus.value = 'success';
    }

    emit('success');
  } catch (e) {
    submitStatus.value = 'error';
    error.value = e.message;
  }
};

const publishLive = draft =>
  Promise.all([
    publishCourse({
      draft,
      publishData: getPublishData(),
      publishTo: 'production'
    }),
    publishCourse({
      draft,
      publishData: getPublishData(),
      publishTo: 'test'
    })
  ]);

const publishInternal = draft =>
  publishCourse({
    draft,
    publishData: getPublishData(),
    publishTo: 'test'
  });

const copyToClipboard = async text => {
  return await navigator.clipboard.writeText(text);
};

const submitMessage = computed(() => {
  switch (submitStatus.value) {
    case 'pending':
      return 'Publishing...';
    case 'success':
      return 'Success!';
    case 'error':
      return error.value || 'Something went wrong.';
    default:
      return '\xa0';
  }
});
</script>
